import { Badge } from "@/components/ui/badge";

import type { SoundRenderProfile } from "./contracts";

export function SoundRenderProfilesPanel({
  profiles,
}: {
  profiles: SoundRenderProfile[];
}) {
  return (
    <section className="space-y-3 rounded-md border p-4">
      <div>
        <h2 className="font-semibold">Applied render profiles</h2>
        <p className="text-sm text-muted-foreground">
          Exact ElevenLabs model, settings, retry, seed, and QC policy frozen
          into each new Echo.
        </p>
      </div>
      {profiles.length ? (
        <div className="grid gap-3 xl:grid-cols-2">
          {profiles.map((profile) => (
            <article
              key={profile.profile_id}
              className="space-y-3 rounded-md border p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium">{profile.profile_id}</h3>
                <Badge variant="secondary">{profile.model_id}</Badge>
                <Badge variant="outline">{profile.output_format}</Badge>
                <Badge
                  variant={
                    profile.final_audio_qc.mode === "enforce"
                      ? "default"
                      : "outline"
                  }
                >
                  final QC {profile.final_audio_qc.mode.replaceAll("_", " ")}
                </Badge>
              </div>

              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <ProfileValue
                  label="Profile settings"
                  value={compactObject(profile.voice_settings)}
                />
                <ProfileValue
                  label="Narration delivery"
                  value={deliveryLabel(profile)}
                />
                <ProfileValue
                  label="Seed policy"
                  value={compactObject(profile.seed_modes)}
                />
                <ProfileValue
                  label="Batch"
                  value={`${profile.joined_batch.max_characters} chars · ${profile.joined_batch.max_duration_seconds}s max`}
                />
                <ProfileValue
                  label="Retry"
                  value={`${profile.retry_policy.max_attempts} attempts · fragment fallback ${yesNo(profile.retry_policy.per_fragment_fallback)}`}
                />
                <ProfileValue
                  label="Narration signal QC"
                  value={`${profile.qc_policy.policy_id} · joined ${yesNo(profile.qc_policy.joined_required)} · fragments ${yesNo(profile.qc_policy.per_fragment_required)} · ${Object.keys(profile.qc_policy.thresholds ?? {}).length} frozen thresholds`}
                />
                <ProfileValue
                  label="Coverage"
                  value={`${(profile.coverage.minimum_ratio * 100).toFixed(0)}% · ${profile.coverage.failure_action}`}
                />
                <ProfileValue
                  label="Final mix"
                  value={finalMixLabel(profile)}
                />
                <ProfileValue
                  label="Release"
                  value={profile.catalog_release_id ?? "unavailable"}
                />
              </div>

              <div className="break-all font-mono text-[11px] text-muted-foreground">
                {profile.profile_digest}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="text-sm text-destructive">
          No applied signed voice render profile is available.
        </p>
      )}
    </section>
  );
}

function ProfileValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded bg-muted/50 p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

function compactObject(value: Record<string, string | number | boolean>) {
  return Object.entries(value)
    .map(([key, item]) => `${key.replaceAll("_", " ")} ${String(item)}`)
    .join(" · ");
}

function deliveryLabel(profile: SoundRenderProfile) {
  if (!profile.performance) return "Legacy delivery";
  const { mode, default_delivery, compiler_version, accent } =
    profile.performance;
  const label = mode === "directed" ? "Directed" : "Plain";
  const accentLabel = accent === "australian" ? " · Australian accent" : "";
  return `${label}${accentLabel} · default ${default_delivery} · ${compiler_version}`;
}

function finalMixLabel(profile: SoundRenderProfile) {
  const qc = profile.final_audio_qc;
  const bounds =
    qc.min_integrated_lufs == null || qc.max_integrated_lufs == null
      ? "measurement only"
      : `${qc.min_integrated_lufs} to ${qc.max_integrated_lufs} LUFS · peak ≤ ${qc.max_true_peak_dbfs} dBFS`;
  return `${qc.mode.replaceAll("_", " ")} · ${bounds} · normalize ${yesNo(qc.normalize)}`;
}

function yesNo(value: boolean) {
  return value ? "yes" : "no";
}
